import type { Timestamp } from 'firebase/firestore';

/**
 * Presença no onboarding de SI — a apresentação é ao vivo (o SI compartilha a tela no 2º dia
 * pós-admissão), então o que importa é quem participou, não quem abriu o documento. Conta por
 * pessoa no geral: participou de tudo numa sessão = onboarding feito, em qualquer versão
 * (assunto novo vai por postagem/documento, não se refaz o onboarding). Decisões do SI.
 */
export type PresencaOnboarding = 'completa' | 'parcial' | 'faltou';

export interface ParticipanteOnboarding {
  colaboradorId: string;
  nome: string;
  presenca: PresencaOnboarding;
  /** Só na parcial: "HH:mm" em que entrou (entrou depois) e/ou saiu (saiu antes); '' = marcou sem saber a hora. */
  entrouAs: string | null;
  saiuAs: string | null;
  observacao: string;
}

export interface SessaoOnboarding {
  id: string;
  /** Documento de onboarding apresentado (si_publicacoes). */
  documentoId: string;
  versao: number;
  /** "AAAA-MM-DD" + "HH:mm" — data da apresentação, não de quando foi registrada. */
  data: string;
  inicio: string;
  fim: string;
  apresentador: { id: string; name: string };
  participantes: ParticipanteOnboarding[];
  criadoPor?: { id: string; name: string };
  criadoEm?: Timestamp;
  atualizadoEm?: Timestamp;
}

/** Registro que abre sozinho ao fechar a apresentação: quando foi (o resto o SI confirma). */
export interface RascunhoSessaoOnboarding {
  data: string;
  inicio: string;
  fim: string;
}

/** Abertura curta (conferir o material) não pede registro de sessão — e mesmo acima disso o SI
 * pode não registrar (estudo, revisão, esqueceu aberto). Decisão do SI. */
export const MINUTOS_MINIMOS_SESSAO = 30;

/** semMinimo: o SI pediu o registro na hora ("Registrar esta sessão"), mesmo com pouco tempo aberto. */
export function rascunhoDaApresentacao(inicio: Date, fim: Date, semMinimo = false): RascunhoSessaoOnboarding | null {
  if (!semMinimo && fim.getTime() - inicio.getTime() < MINUTOS_MINIMOS_SESSAO * 60 * 1000) return null;
  const hora = (d: Date) => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return { data: inicio.toLocaleDateString('sv-SE'), inicio: hora(inicio), fim: hora(fim) };
}

export interface PendenciaOnboarding {
  colaboradorId: string;
  nome: string;
  presenca: Exclude<PresencaOnboarding, 'completa'>;
  /** Última sessão em que a pessoa apareceu (a que vale pra lista). */
  data: string;
  detalhe: string;
}

export function detalhePresenca(participante: ParticipanteOnboarding): string {
  if (participante.presenca === 'faltou') return participante.observacao ? `"${participante.observacao}"` : 'não veio';
  if (participante.presenca === 'completa') return 'participou de tudo';
  const partes = [
    participante.entrouAs !== null ? (participante.entrouAs ? `entrou às ${participante.entrouAs}` : 'entrou depois') : null,
    participante.saiuAs !== null ? (participante.saiuAs ? `saiu às ${participante.saiuAs}` : 'saiu antes') : null,
  ].filter(Boolean);
  return partes.join(' · ') || 'parcial';
}

/**
 * Quem ainda não completou: aparece em alguma sessão e nunca "participou de tudo". Vale o
 * registro mais recente da pessoa; participou de tudo em qualquer sessão = sai da lista.
 */
export function pendenciasOnboarding(sessoes: SessaoOnboarding[]): PendenciaOnboarding[] {
  const completas = new Set<string>();
  const ultima = new Map<string, { participante: ParticipanteOnboarding; data: string }>();
  const ordenadas = [...sessoes].sort((a, b) => `${a.data} ${a.inicio}`.localeCompare(`${b.data} ${b.inicio}`));
  for (const sessao of ordenadas) {
    for (const participante of sessao.participantes) {
      if (participante.presenca === 'completa') completas.add(participante.colaboradorId);
      ultima.set(participante.colaboradorId, { participante, data: sessao.data });
    }
  }
  return Array.from(ultima.values())
    .filter(({ participante }) => !completas.has(participante.colaboradorId))
    .map(({ participante, data }) => ({
      colaboradorId: participante.colaboradorId,
      nome: participante.nome,
      presenca: participante.presenca as Exclude<PresencaOnboarding, 'completa'>,
      data,
      detalhe: detalhePresenca(participante),
    }))
    .sort((a, b) => b.data.localeCompare(a.data) || a.nome.localeCompare(b.nome, 'pt-BR'));
}

/** Contagem pra uma linha de resumo ("1 completo · 2 parciais · 1 faltou"). */
export function contagemPresenca(participantes: ParticipanteOnboarding[]) {
  return {
    completa: participantes.filter((p) => p.presenca === 'completa').length,
    parcial: participantes.filter((p) => p.presenca === 'parcial').length,
    faltou: participantes.filter((p) => p.presenca === 'faltou').length,
  };
}
